import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Chip,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import GuideButton from '../../common/guide/GuideButton';
import { useAuth } from '../../../contexts/AuthContext';
import { BrandService, CreateInventoryBrandDto, InventoryBrandDto } from './services';
import { useTranslation } from 'react-i18next';

// Marka veri yapısı (SQL'den gelen)
interface InventoryBrand {
  id: string;
  name: string;
  description?: string;
  website?: string;
  createdAt: string;
  branchesId?: string | null;
}

interface Branch {
  id: string;
  name: string;
}

interface InventoryBrandModalProps {
  open: boolean;
  onClose: () => void;
  branchId?: string;
  isManager?: boolean;
}

const InventoryBrandModal: React.FC<InventoryBrandModalProps> = ({ 
  open, 
  onClose, 
  branchId, 
  isManager = false 
}) => {
  const { t } = useTranslation('accounting');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches, userDetails } = useAuth();
  
  const [brandName, setBrandName] = useState('');
  const [brandDescription, setBrandDescription] = useState('');
  const [brandWebsite, setBrandWebsite] = useState('');
  const [error, setError] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<InventoryBrand | null>(null);
  const [brands, setBrands] = useState<InventoryBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  
  // Silme onay modalı için state'ler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState<InventoryBrand | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Şirket ID'sini, şubeleri ve markaları yükleme
  useEffect(() => {
    const loadCompanyAndData = async () => {
      try {
        setInitialLoading(true);
        
        const cId = userDetails?.companyId;
        setCompanyId(cId || null);
        
        if (!cId) {
          setInitialLoading(false);
          return;
        }
        
        // Markaları yükle (SQL'den)
        await loadInventoryBrands(cId);
        
      } catch (error) {
        console.error('Şirket veya veri yüklenirken hata:', error);
        setInitialLoading(false);
      }
    };
    
    if (open) {
      loadCompanyAndData();
    }
  }, [open, branchId, userDetails]);
  
  // ✅ Şube müdürü için otomatik şube seçimi (branches değiştiğinde)
  useEffect(() => {
    if (isManager && branchId && branches.length > 0) {
      const managerBranch = branches.find(branch => branch.id === branchId);
      if (managerBranch) {
        setSelectedBranch(managerBranch);
        console.log(`Şube müdürü için otomatik şube seçildi: ${managerBranch.name}`);
      }
    }
  }, [branches, isManager, branchId]);
  
  // Ürün markalarını yükleme (SQL'den)
  const loadInventoryBrands = async (companyId: string) => {
    try {
      console.log('✅ [SQL] Ürün markaları SQL\'den yükleniyor...');
      
      const brandsData = await BrandService.getAllBrands(companyId);
      
      console.log('✅ [SQL] Markalar başarıyla yüklendi:', brandsData.length);
      
      // DTO'dan interface'e dönüştür
      const brandsList: InventoryBrand[] = brandsData.map((brand: InventoryBrandDto) => ({
        id: brand.id,
        name: brand.name || '',
        description: brand.description || '',
        website: brand.website || '',
        createdAt: brand.createdAt || '',
        branchesId: null // Şimdilik şube desteği yok
      }));
      
      console.log('✅ [SQL] İşlenmiş markalar:', brandsList);
      
      // Markaları isme göre sıralayalım
      brandsList.sort((a, b) => a.name.localeCompare(b.name));
      
      setBrands(brandsList);
    } catch (error) {
      console.error('❌ [SQL] Marka verileri yüklenirken hata:', error);
      setSnackbarMessage('Markalar yüklenirken hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setInitialLoading(false);
      setLoading(false);
    }
  };
  
  // Formun temizlenmesi
  const resetForm = () => {
    setBrandName('');
    setBrandDescription('');
    setBrandWebsite('');
    setError('');
  };
  
  // Modal kapatılırken çalışacak handler
  const handleClose = () => {
    resetForm();
    setSelectedBrand(null);
    onClose();
  };
  
  // Marka seçimi
  const handleSelectBrand = (brand: InventoryBrand) => {
    setSelectedBrand(brand);
    setBrandName(brand.name);
    setBrandDescription(brand.description || '');
    setBrandWebsite(brand.website || '');
    
    // Eğer bir marka seçildiyse, onun şubesini otomatik olarak seç
    if (brand) {
      if (brand.branchesId) {
        // Seçilen markanın şubesi varsa o şubeyi bul ve seç
        const brandBranch = branches.find(b => b.id === brand.branchesId);
        if (brandBranch) {
          setSelectedBranch(brandBranch);
        }
      }
    }
  };
  
  // Marka silme onay modalını aç
  const handleDeleteBrand = (brand: InventoryBrand, event: React.MouseEvent) => {
    event.stopPropagation();
    setBrandToDelete(brand);
    setDeleteConfirmOpen(true);
  };
  
  // Silme onay modalını kapat
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setBrandToDelete(null);
  };
  
  // Marka silme işlemini onayla ve gerçekleştir
  const confirmDeleteBrand = async () => {
    if (!brandToDelete || !companyId) {
      return;
    }
    
    try {
      setIsDeleting(true);
      
      console.log('✅ [SQL] Marka siliniyor:', brandToDelete.id);
      
      // Markayı SQL'den sil
      await BrandService.deleteBrand(companyId, brandToDelete.id);
      
      // Başarı mesajı
      setSnackbarMessage('Marka başarıyla silindi');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Eğer silinen marka seçili markaysa, seçimi temizle
      if (selectedBrand?.id === brandToDelete.id) {
        setSelectedBrand(null);
        resetForm();
      }
      
      // Modal'ı kapat
      handleCloseDeleteConfirm();
      
      // Markaları yeniden yükle
      await loadInventoryBrands(companyId);
      
    } catch (error) {
      console.error('❌ [SQL] Marka silinirken hata:', error);
      setSnackbarMessage('Marka silinirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Yeni marka ekleme veya mevcut markayı güncelleme (SQL)
  const handleAddBrand = async () => {
    if (!brandName.trim()) {
      setError('Marka adı boş olamaz');
      return;
    }
    
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı');
      return;
    }
    
    try {
      setLoading(true);
      
      if (selectedBrand) {
        // Mevcut markayı güncelle
        console.log('✅ [SQL] Marka güncelleniyor...');
        await BrandService.updateBrand({
          companyId: companyId, // ✅ MULTI-TENANT: companyId eklendi
          id: selectedBrand.id,
          name: brandName.trim(),
          description: brandDescription.trim() || undefined,
          website: brandWebsite.trim() || undefined
        });
        setSnackbarMessage('Marka başarıyla güncellendi');
      } else {
        // Yeni marka ekle
        console.log('✅ [SQL] Yeni marka ekleniyor...');
        const createDto: CreateInventoryBrandDto = {
          companyId: companyId,
          name: brandName.trim(),
          description: brandDescription.trim() || undefined,
          website: brandWebsite.trim() || undefined,
          isActive: true
        };
        await BrandService.createBrand(createDto);
        setSnackbarMessage('Marka başarıyla eklendi');
      }
      
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Formu sıfırla ve seçimi temizle
      resetForm();
      setSelectedBrand(null);
      
      // Markaları yeniden yükle
      await loadInventoryBrands(companyId);
      
    } catch (error) {
      console.error('❌ [SQL] Marka kaydedilirken hata:', error);
      setError('Marka kaydedilirken bir hata oluştu');
      setSnackbarMessage('Marka kaydedilirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Şube adını getir
  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return 'Genel';
    
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Bilinmeyen Şube';
  };
  
  // Buton metni
  const getButtonText = () => {
    if (loading) return t('stock.common.saving');
    return selectedBrand ? t('stock.modals.brand.actions.update') : t('stock.modals.brand.actions.add');
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('stock.modals.brand.title')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GuideButton guideKey="inventory.brands" />
            <IconButton edge="end" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ overflow: { xs: 'auto', md: 'hidden' }, p: 0 }}>
        <Box sx={{ p: { xs: 2, md: 3 }, height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'hidden' } }}>
          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
            {/* Form Alanı */}
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {selectedBrand ? t('stock.modals.brand.form.editTitle') : t('stock.modals.brand.form.addTitle')}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                {/* Şube seçim alanı */}
                <Autocomplete
                  fullWidth
                  value={selectedBranch}
                  onChange={(event, newValue) => {
                    if (!(isManager && branchId)) {
                      setSelectedBranch(newValue);
                    }
                  }}
                  options={branches}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.modals.brand.form.branchOptional')}
                      size="small"
                      placeholder={t('stock.modals.brand.form.selectBranch')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        readOnly: isManager && branchId ? true : false
                      }}
                      disabled={!!(isManager && branchId)}
                      sx={{ mb: 2 }}
                    />
                  )}
                  disableClearable={!!(isManager && branchId)}
                  disabled={!!(isManager && branchId)}
                />
                
                <TextField
                  autoFocus
                  margin="dense"
                  id="brandName"
                  label={t('stock.modals.brand.form.name')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={brandName}
                  onChange={(e) => {
                    setBrandName(e.target.value);
                    setError('');
                  }}
                  error={!!error}
                  helperText={error}
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
                
                <TextField
                  margin="dense"
                  id="brandDescription"
                  label={t('stock.modals.brand.form.description')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={brandDescription}
                  onChange={(e) => setBrandDescription(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={loading}
                  multiline
                  rows={2}
                />
                
                <TextField
                  margin="dense"
                  id="brandWebsite"
                  label={t('stock.modals.brand.form.website')}
                  type="url"
                  fullWidth
                  variant="outlined"
                  value={brandWebsite}
                  onChange={(e) => setBrandWebsite(e.target.value)}
                  placeholder="https://example.com"
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  {selectedBrand && (
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setSelectedBrand(null);
                        resetForm();
                      }}
                      disabled={loading}
                    >
                      {t('stock.common.cancel')}
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleAddBrand}
                    disabled={!brandName.trim() || loading}
                    fullWidth={!selectedBrand}
                    sx={{ ml: selectedBrand ? 2 : 0 }}
                    startIcon={loading ? <CircularProgress size={24} /> : <AddIcon />}
                  >
                    {getButtonText()}
                  </Button>
                </Box>
              </Paper>
            </Grid>
            
            {/* Tablo/Kart Görünüm Alanı */}
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {t('stock.modals.brand.list.title')}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                {brands.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('stock.modals.brand.list.empty')}
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('stock.modals.brand.list.total', { count: brands.length })}
                      </Typography>
                    </Box>
                    
                    {/* Desktop: Table View */}
                    {!isMobile && (
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 400 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('stock.modals.brand.table.name')}</TableCell>
                              <TableCell>{t('stock.modals.brand.table.description')}</TableCell>
                              <TableCell>{t('stock.modals.brand.table.website')}</TableCell>
                              <TableCell align="right">{t('stock.modals.brand.table.actions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {brands.map((brand) => (
                              <TableRow 
                                key={brand.id} 
                                hover
                                sx={{ 
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                  ...(selectedBrand?.id === brand.id ? 
                                    { backgroundColor: 'rgba(25, 118, 210, 0.12)' } : {}),
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleSelectBrand(brand)}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <BrandingWatermarkIcon sx={{ 
                                      fontSize: '1rem', 
                                      mr: 1, 
                                      color: 'primary.main',
                                      opacity: 0.8
                                    }} />
                                    <Typography variant="body2" sx={{ 
                                      fontWeight: selectedBrand?.id === brand.id ? 'bold' : 'normal' 
                                    }}>
                                      {brand.name}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary" 
                                    sx={{ 
                                      maxWidth: 150,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {brand.description || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary"
                                    sx={{ 
                                      maxWidth: 120,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {brand.website ? (
                                      <a 
                                        href={brand.website} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ color: 'inherit', textDecoration: 'underline' }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {brand.website.replace(/(^\w+:|^)\/\//, '')}
                                      </a>
                                    ) : (
                                      '-'
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleDeleteBrand(brand, e)}
                                    sx={{ color: 'error.main' }}
                                    disabled={loading}
                                    title={t('stock.common.delete')}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                    
                    {/* Mobile: Card View */}
                    {isMobile && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {brands.map((brand) => (
                          <Card 
                            key={brand.id}
                            sx={{ 
                              border: selectedBrand?.id === brand.id 
                                ? '2px solid' 
                                : '1px solid',
                              borderColor: selectedBrand?.id === brand.id 
                                ? 'primary.main' 
                                : 'divider',
                              bgcolor: selectedBrand?.id === brand.id 
                                ? 'rgba(25, 118, 210, 0.08)' 
                                : 'background.paper',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleSelectBrand(brand)}
                          >
                            <CardContent sx={{ pb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <BrandingWatermarkIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight="bold"
                                  sx={{ flex: 1 }}
                                >
                                  {brand.name}
                                </Typography>
                              </Box>
                              
                              {brand.description && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('stock.modals.brand.form.description')}:
                                  </Typography>
                                  <Typography variant="body2">
                                    {brand.description}
                                  </Typography>
                                </Box>
                              )}
                              
                              {brand.website && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('stock.modals.brand.form.website')}:
                                  </Typography>
                                  <Typography variant="body2">
                                    <a 
                                      href={brand.website} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ color: 'inherit', textDecoration: 'underline' }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {brand.website}
                                    </a>
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                            
                            <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={(e) => handleDeleteBrand(brand, e)}
                                disabled={loading}
                              >
                                {t('stock.common.delete')}
                              </Button>
                            </CardActions>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>{t('stock.common.close')}</Button>
      </DialogActions>

      {/* Silme Onay Modalı */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">{t('stock.modals.brand.delete.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('stock.modals.brand.delete.irreversible')}
          </Alert>
          <Typography variant="body1" gutterBottom>
            <strong>"{brandToDelete?.name}"</strong> {t('stock.modals.brand.delete.confirmQuestion')}
          </Typography>
          {brandToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('stock.modals.brand.delete.details')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • {t('stock.modals.brand.table.name')}: {brandToDelete.name}
              </Typography>
              {brandToDelete.description && (
                <Typography variant="body2">
                  • {t('stock.modals.brand.table.description')}: {brandToDelete.description}
                </Typography>
              )}
              {brandToDelete.website && (
                <Typography variant="body2">
                  • {t('stock.modals.brand.table.website')}: {brandToDelete.website}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseDeleteConfirm} 
            disabled={isDeleting}
            variant="outlined"
          >
            {t('stock.common.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteBrand} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? t('stock.modals.brand.delete.deleting') : t('stock.modals.brand.delete.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default InventoryBrandModal; 